import { Theme } from '@mui/material/styles'

/**
 * Centralized scrollbar styles
 * Can be used with sx prop or styled-components
 */

interface ScrollbarOptions {
  width?: string
  height?: string
  trackColor?: string
  thumbColor?: string
  borderRadius?: string
}

/**
 * Generate scrollbar styles for use in styled-components or sx props
 * Automatically adapts to theme mode (dark/light)
 */
export const scrollbarStyles = (theme: Theme, options: ScrollbarOptions = {}) => {
  const {
    width = '6px',
    height = '6px',
    trackColor,
    thumbColor,
    borderRadius = '6px',
  } = options

  const defaultTrackColor = theme.palette.mode === 'dark'
    ? 'inset 0 0 6px rgba(0,0,0,0.3)'
    : 'inset 0 0 6px rgba(255,255,255,0.3)'

  const defaultThumbColor = theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.5)'
    : 'rgba(0,0,0,0.5)'

  return {
    overflowY: 'auto' as const,
    '&::-webkit-scrollbar': {
      width,
      height,
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: trackColor || defaultTrackColor,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumbColor || defaultThumbColor,
      borderRadius,
    },
    // Firefox scrollbar
    scrollbarWidth: 'thin' as const,
    scrollbarColor: `${thumbColor || defaultThumbColor} transparent`,
  }
}

/**
 * Thin scrollbar variant (4px width)
 */
export const thinScrollbarStyles = (theme: Theme) =>
  scrollbarStyles(theme, { width: '4px', height: '4px' })

/**
 * Hidden scrollbar (scrolling still works)
 */
export const hiddenScrollbarStyles = () => ({
  overflowY: 'auto' as const,
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none', // IE and Edge
  scrollbarWidth: 'none' as const, // Firefox
})

/**
 * Horizontal scrollbar styles
 */
export const horizontalScrollbarStyles = (theme: Theme, options: ScrollbarOptions = {}) => {
  const styles = scrollbarStyles(theme, options)
  return {
    ...styles,
    overflowX: 'auto' as const,
    overflowY: 'hidden' as const,
  }
}

/**
 * Auto-hide scrollbar on hover
 */
export const autoHideScrollbarStyles = (theme: Theme) => ({
  overflowY: 'auto' as const,
  '&::-webkit-scrollbar': {
    width: '6px',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  '&:hover::-webkit-scrollbar': {
    opacity: 1,
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: theme.palette.mode === 'dark'
      ? 'inset 0 0 6px rgba(0,0,0,0.3)'
      : 'inset 0 0 6px rgba(255,255,255,0.3)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.5)'
      : 'rgba(0,0,0,0.5)',
    borderRadius: '6px',
  },
})
